import * as React from 'react';
import { Box } from '@material-ui/core';
import {
  AutocompleteArrayInput,
  BooleanInput,
  DateField,
  FormDataConsumer,
  Record,
  ReferenceArrayInput,
  required,
  TextField,
} from 'react-admin';
import { titleRenderer, titleWithFlagRenderer } from '../../common/optionRendererFactory';
import {
  BelongsToMany,
  CustomDateInput,
  RelatedEntityField,
  RelatedEntityInput,
  TopicTypeInput,
  WrapInput,
} from '../../common/custom-components';
import TopicType from '../../common/topicType';
import { requiredForPublishing } from '../../common/validators';
import { useCheckAbilities } from '../../common/custom-hooks';
import { subject } from '@casl/ability';
import { useFormState } from 'react-final-form';

interface Props {
  basePath: string;
  record: Record;
  resource: string;
}

const GeneralSection = (props: Props) => {
  const { values } = useFormState();
  const { ...fieldProps } = props;
  const isDateBased = props.record.type === TopicType.DATE_BASED;
  const topicValidator = requiredForPublishing(required());

  const [canPublish, canUpdateDate, canAssignToParent] = useCheckAbilities(can => [
    can('update', subject('Daily', props.record), 'isPublished'),
    can('update', subject('Daily', props.record), 'date'),
    can('assignToParent', subject('Daily', props.record)),
  ]);

  return (
    <Box flexGrow={1}>
      <Box display={'flex'}>
        <WrapInput {...fieldProps} mr={1} flexGrow={1}>
          <BooleanInput disabled={!canPublish} source="isPublished" />
        </WrapInput>

        <WrapInput formInputClassName={null} {...fieldProps}>
          {fieldProps.record.createdAt && <DateField source="createdAt" />}
        </WrapInput>
        <WrapInput formInputClassName={null} ml={1} {...fieldProps}>
          {fieldProps.record.createdBy && <TextField source="createdBy" />}
        </WrapInput>
        <WrapInput formInputClassName={null} ml={1} {...fieldProps}>
          {fieldProps.record.updatedAt && <DateField source="updatedAt" />}
        </WrapInput>
        <WrapInput formInputClassName={null} ml={1} {...fieldProps}>
          {fieldProps.record.updatedBy && <TextField source="updatedBy" />}
        </WrapInput>
      </Box>

      <Box display={'flex'}>
        <WrapInput {...fieldProps} mr={1}>
          <TopicTypeInput source={'type'} disabled={true} />
        </WrapInput>
        <WrapInput {...fieldProps}>
          <CustomDateInput
            source="date"
            dateOnly={true}
            validate={isDateBased ? required() : undefined}
            isRequired={isDateBased}
            options={{
              disabled: !canUpdateDate,
              // minDate: '2020-01-01',
              // maxDate: '2020-12-31',
              views: ['month', 'date'],
            }}
          />
        </WrapInput>
      </Box>

      {canAssignToParent && (
        <WrapInput {...fieldProps}>
          <FormDataConsumer>
            {({ formData }) => (
              <ReferenceArrayInput
                source={'topicIds'}
                reference={'topic'}
                resource={props.resource}
                validate={topicValidator}
                filter={{
                  type: props.record.type,
                  lang: formData.language,
                }}
              >
                <AutocompleteArrayInput optionText={titleWithFlagRenderer} />
              </ReferenceArrayInput>
            )}
          </FormDataConsumer>
        </WrapInput>
      )}
      <WrapInput {...fieldProps}>
        <BelongsToMany
          addLabel={!canAssignToParent}
          source={'topicIds'}
          reference={'topic'}
          optionRenderer={titleWithFlagRenderer}
        />
      </WrapInput>

      {props.record.quizId && (
        <WrapInput {...fieldProps} mt={2}>
          <RelatedEntityField reference={'daily'} source={'quizId'} optionTextRenderer={titleRenderer} />
        </WrapInput>
      )}
      {props.record.type === TopicType.QUIZ && (
        <WrapInput {...fieldProps} mt={2}>
          <RelatedEntityInput
            filter={{
              lang: values.language,
              type: TopicType.INDEX_BASED,
            }}
            optionTextRenderer={titleRenderer}
            reference={'daily'}
            source={'quizForId'}
            label={'resources.daily.quiz_source_daily'}
          />
        </WrapInput>
      )}
    </Box>
  );
};

export default GeneralSection;
